import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Package, Eye, RotateCcw, Trash2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Order } from '../utils/orderService';
import { useOrders, OrderListType, OrderFilterExt } from '../hooks/useOrders';
import {
  getStatusConfig,
  getPayMethodLabel,
  getDurationLabel,
  calcActualPrice,
  calcOriginalPrice,
  getSourceLabel,
  getTeacherPackageLabel,
  getTeacherPackageName,
  getTeacherPackageTypeLabel,
  calcTeacherActualPrice,
  calcTeacherOriginalPrice,
} from '../utils/orderUtils';
import { formatDateTime, getBeijingToday } from '../utils/timeUtils';

export interface OrderListProps {
  /** 场景类型 */
  type: OrderListType;
  /** 目标用户ID，type=admin-student/admin-teacher 时必传 */
  targetUid?: string;
  /** 扩展筛选条件 */
  filterExt?: OrderFilterExt;
  /** 展示模式：page=页面级（带完整分页），modal=弹窗内嵌（紧凑样式） */
  mode?: 'page' | 'modal';
  /** 每页条数，默认10 */
  pageSize?: number;
  /** 是否显示操作列 */
  showActions?: boolean;
  /** 退款回调 */
  onRefund?: (order: any) => void;
  /** 删除回调 */
  onDelete?: (order: any) => void;
  /** 查看退款详情回调 */
  onViewRefundDetail?: (order: any) => void;
  /** 是否启用，默认true */
  enabled?: boolean;
  /** 是否显示导出Excel按钮 */
  showExport?: boolean;
  /** 导出文件名 */
  exportFileName?: string;
  /** 是否显示批量选择复选框 */
  showBatchSelect?: boolean;
  /** 批量删除回调 */
  onBatchDelete?: (ids: string[]) => void;
}

function isTeacherType(type: OrderListType): boolean {
  return type === 'teacher-center' || type === 'admin-all-teacher' || type === 'admin-teacher';
}

export default function OrderList({
  type,
  targetUid,
  filterExt,
  mode = 'page',
  pageSize = 10,
  showActions = true,
  onRefund,
  onDelete,
  onViewRefundDetail,
  enabled = true,
  showExport = false,
  exportFileName = '订单',
  showBatchSelect = false,
  onBatchDelete,
}: OrderListProps) {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const {
    paginatedOrders,
    filteredOrders,
    loading,
    currentPage,
    totalPages,
    setCurrentPage,
    refresh,
  } = useOrders({
    type,
    targetUid,
    filterExt,
    pageSize: mode === 'modal' ? 100 : pageSize,
    enabled,
  });

  // 监听订单更新事件，自动刷新列表
  useEffect(() => {
    const handleOrderUpdate = () => {
      refresh();
    };
    window.addEventListener('order-updated', handleOrderUpdate);
    return () => {
      window.removeEventListener('order-updated', handleOrderUpdate);
    };
  }, [refresh]);

  const isTeacher = isTeacherType(type);

  // 根据场景决定显示哪些列
  const columns = useMemo(() => {
    if (isTeacher) {
      // 教师订单列：与学员订单结构一致，仅将学员信息替换为教师信息
      const isAdminAllTeacher = type === 'admin-all-teacher';
      return [
        { key: 'checkbox', label: '', visible: showBatchSelect },
        { key: 'orderNo', label: '订单号', visible: true },
        { key: 'teacherName', label: '教师姓名', visible: isAdminAllTeacher },
        { key: 'teacherPhone', label: '手机号', visible: isAdminAllTeacher },
        { key: 'packageName', label: '套餐名称', visible: true },
        { key: 'duration', label: '套餐类型', visible: true },
        { key: 'originalPrice', label: '价格', visible: true },
        { key: 'pointsDeducted', label: 'U币抵扣', visible: true },
        { key: 'actualPrice', label: '实付金额', visible: true },
        { key: 'status', label: '订单状态', visible: true },
        { key: 'payMethod', label: '支付方式', visible: true },
        { key: 'createdAt', label: '创建时间', visible: true },
        { key: 'payTime', label: '支付时间', visible: true },
        { key: 'source', label: '渠道', visible: isAdminAllTeacher },
        { key: 'actions', label: '操作', visible: isAdminAllTeacher && showActions },
      ].filter(c => c.visible);
    }

    // 学员订单列：基础列 + admin-all-student 专属管理列
    const isAdminAll = type === 'admin-all-student';
    const isUserCenter = type === 'student-center';
    const isAdminStudent = type === 'admin-student';
    return [
      { key: 'checkbox', label: '', visible: showBatchSelect },
      { key: 'orderNo', label: '订单号', visible: true },
      { key: 'userName', label: '学员姓名', visible: isAdminAll },
      { key: 'userPhone', label: '手机号', visible: isAdminAll },
      { key: 'packageName', label: '套餐名称', visible: true },
      { key: 'duration', label: '套餐类型', visible: true },
      { key: 'originalPrice', label: '价格', visible: true },
      { key: 'pointsDeducted', label: 'U币抵扣', visible: isAdminAll || isUserCenter || isAdminStudent },
      { key: 'actualPrice', label: '实付金额', visible: true },
      { key: 'status', label: '订单状态', visible: true },
      { key: 'payMethod', label: '支付方式', visible: true },
      { key: 'createdAt', label: '创建时间', visible: true },
      { key: 'payTime', label: '支付时间', visible: true },
      { key: 'source', label: '渠道', visible: isAdminAll },
      { key: 'actions', label: '操作', visible: isAdminAll && showActions },
    ].filter(c => c.visible);
  }, [type, showActions, showBatchSelect, isTeacher]);

  const handleOrderClick = (orderId: string) => {
    navigate(`/order/${orderId}`);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedOrders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedOrders.map(o => o.id)));
    }
  };

  // 导出Excel
  const handleExport = () => {
    const rows = filteredOrders.map(order => {
      if (isTeacher) {
        const to = order as any;
        return {
          '订单号': to.orderNo,
          '教师姓名': (to as any).teacherName || '-',
          '手机号': (to as any).teacherPhone || '-',
          '套餐名称': getTeacherPackageName(to),
          '套餐类型': getTeacherPackageTypeLabel(to.packageType),
          '价格': calcTeacherOriginalPrice(to).toFixed(2),
          '实付金额': calcTeacherActualPrice(to).toFixed(2),
          '订单状态': getStatusConfig(to.status).label,
          '支付方式': getPayMethodLabel(to as any),
          '创建时间': formatDateTime(to.createdAt),
          '支付时间': to.payTime ? formatDateTime(to.payTime) : '-',
          '到期时间': to.expireDate || '-',
          '渠道': getSourceLabel((to as any).source || ''),
        };
      }
      const so = order as Order;
      return {
        '订单号': so.orderNo,
        '学员姓名': so.userName || '-',
        '手机号': so.userPhone || '-',
        '套餐名称': `${so.orgName}-${so.catName}-${so.levelText}`,
        '套餐类型': getDurationLabel(so.duration),
        '价格': calcOriginalPrice(so).toFixed(2),
        '实付金额': calcActualPrice(so).toFixed(2),
        '订单状态': getStatusConfig(so.status).label,
        '支付方式': getPayMethodLabel(so),
        '创建时间': formatDateTime(so.createdAt),
        '支付时间': so.payTime ? formatDateTime(so.payTime) : '-',
        '渠道': getSourceLabel(so.source),
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '订单');
    XLSX.writeFile(wb, `${exportFileName}_${getBeijingToday()}.xlsx`);
  };

  const isCompact = mode === 'modal';
  const cellClass = isCompact ? 'px-1.5 py-2 text-xs' : 'px-3 py-3 text-sm';
  const headerClass = isCompact
    ? 'px-1.5 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
    : 'px-3 py-3 text-left text-sm font-medium text-gray-600 whitespace-nowrap';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500">加载中...</span>
      </div>
    );
  }

  return (
    <div className={isCompact ? '' : 'bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'}>
      {/* 工具栏：导出 + 批量删除 */}
      {(showExport || (showBatchSelect && selectedIds.size > 0)) && (
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            {showBatchSelect && selectedIds.size > 0 && (
              <>
                <span className="text-sm text-blue-700">已选择 {selectedIds.size} 条</span>
                <button onClick={() => setSelectedIds(new Set())} className="text-sm text-blue-600 hover:text-blue-800">取消选择</button>
                {onBatchDelete && (
                  <button
                    onClick={() => { onBatchDelete([...selectedIds]); setSelectedIds(new Set()); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" /> 批量删除
                  </button>
                )}
              </>
            )}
          </div>
          {showExport && (
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" /> 导出Excel
            </button>
          )}
        </div>
      )}

      {/* 表格 */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={isCompact ? 'bg-white rounded-lg' : 'bg-gray-50 border-b border-gray-200'}>
            <tr>
              {columns.map(col => {
                if (col.key === 'checkbox') {
                  return (
                    <th key={col.key} className={headerClass}>
                      <input
                        type="checkbox"
                        checked={paginatedOrders.length > 0 && selectedIds.size === paginatedOrders.length}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                  );
                }
                return <th key={col.key} className={headerClass}>{col.label}</th>;
              })}
            </tr>
          </thead>
          <tbody className={isCompact ? 'divide-y divide-gray-200' : 'divide-y divide-gray-100'}>
            {paginatedOrders.map((order) => {
              const statusConfig = getStatusConfig(order.status);
              const actualPrice = isTeacher
                ? calcTeacherActualPrice(order as any)
                : calcActualPrice(order as Order);
              const originalPrice = isTeacher
                ? calcTeacherOriginalPrice(order as any)
                : calcOriginalPrice(order as Order);

              return (
                <motion.tr
                  key={order.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={isCompact ? 'hover:bg-white rounded-lg' : 'hover:bg-gray-50'}
                >
                  {columns.map(col => {
                    switch (col.key) {
                      case 'checkbox':
                        return (
                          <td key={col.key} className={`${cellClass} whitespace-nowrap`}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(order.id)}
                              onChange={() => toggleSelect(order.id)}
                              className="rounded border-gray-300"
                            />
                          </td>
                        );
                      case 'orderNo':
                        return (
                          <td key={col.key} className={`${cellClass} font-mono text-gray-700 whitespace-nowrap`}>
                            <button
                              onClick={() => handleOrderClick(order.id)}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {order.orderNo}
                            </button>
                          </td>
                        );
                      case 'userName':
                        return <td key={col.key} className={`${cellClass} font-medium whitespace-nowrap`}>{(order as Order).userName}</td>;
                      case 'userPhone':
                        return <td key={col.key} className={`${cellClass} text-gray-700 font-mono whitespace-nowrap`}>{(order as Order).userPhone}</td>;
                      case 'teacherName': {
                        const to = order as any;
                        return <td key={col.key} className={`${cellClass} font-medium whitespace-nowrap`}>{to.teacherName || '-'}</td>;
                      }
                      case 'teacherPhone': {
                        const to = order as any;
                        return <td key={col.key} className={`${cellClass} text-gray-700 font-mono whitespace-nowrap`}>{to.teacherPhone || '-'}</td>;
                      }
                      case 'packageName':
                        if (isTeacher) {
                          const to = order as any;
                          return (
                            <td key={col.key} className={`${cellClass} whitespace-nowrap`}>
                              {getTeacherPackageName(to)}
                            </td>
                          );
                        }
                        return (
                          <td key={col.key} className={`${cellClass} whitespace-nowrap`} title={`${(order as Order).orgName}-${(order as Order).catName}-${(order as Order).levelText}`}>
                            {(order as Order).orgName}-{(order as Order).catName}-{(order as Order).levelText}
                          </td>
                        );
                      case 'duration':
                        if (isTeacher) {
                          const to = order as any;
                          // 优先使用 duration 字段（来自 orders 表的 duration_type）
                          const durationType = to.duration || to.packageType;
                          return <td key={col.key} className={`${cellClass} whitespace-nowrap`}>{getTeacherPackageTypeLabel(durationType)}</td>;
                        }
                        return <td key={col.key} className={`${cellClass} whitespace-nowrap`}>{getDurationLabel((order as Order).duration)}</td>;
                      case 'originalPrice':
                        return <td key={col.key} className={`${cellClass} whitespace-nowrap`}>¥{originalPrice.toFixed(2)}</td>;
                      case 'pointsDeducted':
                        return (
                          <td key={col.key} className={`${cellClass} whitespace-nowrap`}>
                            {order.pointsDeducted && order.pointsDeducted > 0
                              ? <span className="text-orange-600">{order.pointsDeducted}分 (-¥{(order.deductionAmount || 0).toFixed(2)})</span>
                              : <span className="text-gray-400">-</span>}
                          </td>
                        );
                      case 'actualPrice':
                        return <td key={col.key} className={`${cellClass} font-bold text-gray-900 whitespace-nowrap`}>¥{actualPrice.toFixed(2)}</td>;
                      case 'status':
                        return (
                          <td key={col.key} className={`${isCompact ? 'px-1.5 py-2' : 'px-3 py-3'} whitespace-nowrap`}>
                            <div className="flex items-center gap-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                                {statusConfig.label}
                              </span>
                              {order.status === 'refunded' && onViewRefundDetail && (
                                <button
                                  onClick={() => onViewRefundDetail(order)}
                                  className="p-0.5 text-blue-600 hover:bg-blue-50 rounded"
                                  title="查看退款详情"
                                >
                                  <Eye className={isCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      case 'payMethod':
                        return <td key={col.key} className={`${cellClass} text-gray-600 whitespace-nowrap`}>{getPayMethodLabel(order as any)}</td>;
                      case 'createdAt':
                        return <td key={col.key} className={`${cellClass} text-gray-500 whitespace-nowrap`}>{formatDateTime(order.createdAt)}</td>;
                      case 'payTime':
                        return <td key={col.key} className={`${cellClass} text-gray-500 whitespace-nowrap`}>{formatDateTime(order.payTime)}</td>;
                      case 'source':
                        return <td key={col.key} className={`${cellClass} whitespace-nowrap`}>{getSourceLabel((order as any).source || '')}</td>;
                      case 'actions':
                        return (
                          <td key={col.key} className={`${isCompact ? 'px-1.5 py-2' : 'px-3 py-3'} whitespace-nowrap`}>
                            <div className="flex items-center justify-center gap-2">
                              {order.status === 'paid' && onRefund && (
                                <button
                                  onClick={() => onRefund(order)}
                                  className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                                  title="退款"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              )}
                              {onDelete && (
                                <button
                                  onClick={() => onDelete(order)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="删除"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      default:
                        return null;
                    }
                  })}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 空状态 */}
      {paginatedOrders.length === 0 && (
        <div className="text-center py-12">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">暂无订单记录</p>
        </div>
      )}

      {/* 分页（仅 page 模式且数据量超过一页时显示） */}
      {mode === 'page' && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="text-sm text-gray-500">共 {filteredOrders.length} 条，第 {currentPage} / {totalPages} 页</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
